import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useFeatureFlagStore } from './featureFlags';

// Persona types that can be switched to (display names)
export type SwitchablePersona =
  | 'admin'
  | 'cohort_manager'
  | 'coach'
  | 'participant'
  | 'partner';

export interface PersonaConfig {
  id: SwitchablePersona;
  label: string;
  shortLabel: string;
  color: string;
  defaultRoute: string;
}

// Configuration for each persona
// Note: admin = internal_admin, cohort_manager = internal_user in DB
export const PERSONA_CONFIG: Record<SwitchablePersona, PersonaConfig> = {
  admin: {
    id: 'admin',
    label: 'Medley Admin',
    shortLabel: 'Admin',
    color: 'red',
    defaultRoute: '/',
  },
  cohort_manager: {
    id: 'cohort_manager',
    label: 'Medley Cohort Manager',
    shortLabel: 'Cohort Mgr',
    color: 'blue',
    defaultRoute: '/', // Same dashboard as admin
  },
  coach: {
    id: 'coach',
    label: 'Coach',
    shortLabel: 'Coach',
    color: 'green',
    defaultRoute: '/coach',
  },
  participant: {
    id: 'participant',
    label: 'Participant',
    shortLabel: 'Participant',
    color: 'orange',
    defaultRoute: '/participant',
  },
  partner: {
    id: 'partner',
    label: 'Partner',
    shortLabel: 'Partner',
    color: 'violet',
    defaultRoute: '/partner',
  },
};

// Navigation item type - can be a simple link or a dropdown with children
export interface NavItem {
  path?: string;
  label: string;
  children?: Array<{ path: string; label: string }>;
}

// Navigation items for each persona
export const PERSONA_NAV_ITEMS: Record<SwitchablePersona, NavItem[]> = {
  admin: [
    { path: '/', label: 'Dashboard' },
    { path: '/cohorts', label: 'Cohorts' },
    { path: '/tasks', label: 'Tasks' },
    { path: '/data-library', label: 'Data Library' },
    { path: '/coach-dashboard', label: 'Coach Dashboard' },
    {
      label: 'Directory',
      children: [
        { path: '/partners', label: 'Partners' },
        { path: '/coaches', label: 'Coaches' },
      ],
    },
    {
      label: 'Admin',
      children: [
        { path: '/users', label: 'Users' },
        { path: '/admin/task-templates', label: 'Templates' },
        { path: '/admin/evergreen-resources', label: 'Evergreen Resources' },
        { path: '/admin/frameworks', label: 'Frameworks' },
        { path: '/admin/absence-email-settings', label: 'Absence Emails' },
        { path: '/admin/impersonation-log', label: 'Impersonation Log' },
        { path: '/admin/compensation-settings', label: 'Compensation Settings' },
      ],
    },
  ],
  cohort_manager: [
    { path: '/', label: 'Dashboard' },
    { path: '/cohorts', label: 'Cohorts' },
    { path: '/tasks', label: 'Tasks' },
    { path: '/data-library', label: 'Data Library' },
    { path: '/coach-dashboard', label: 'Coach Dashboard' },
    {
      label: 'Directory',
      children: [
        { path: '/partners', label: 'Partners' },
        { path: '/coaches', label: 'Coaches' },
      ],
    },
  ],
  coach: [
    { path: '/coach', label: 'Dashboard' },
    { path: '/coach/sessions', label: 'My Sessions' },
    { path: '/coach/resources', label: 'Resources' },
    { path: '/coach/profile', label: 'My Profile' },
  ],
  participant: [
    { path: '/participant', label: 'Dashboard' },
    { path: '/participant/group', label: 'My Group' },
    { path: '/participant/resources', label: 'Resources' },
  ],
  partner: [
    { path: '/partner', label: 'Dashboard' },
    { path: '/partner/cohorts', label: 'Cohorts' },
    { path: '/partner/reports', label: 'Reports' },
  ],
};

// Routes accessible by each persona
export const PERSONA_ALLOWED_ROUTES: Record<SwitchablePersona, string[]> = {
  admin: ['/', '/cohorts', '/tasks', '/data-library', '/coach-dashboard', '/coaches', '/partners', '/users', '/admin'],
  cohort_manager: ['/', '/cohorts', '/tasks', '/data-library', '/coach-dashboard', '/coaches', '/partners'],
  coach: ['/coach', '/coach/sessions', '/coach/resources', '/coach/profile'],
  participant: ['/participant', '/participant/group', '/participant/sessions', '/participant/resources'],
  partner: ['/partner', '/partner/cohorts', '/partner/reports'],
};

// All known app routes (union of all persona routes) for distinguishing
// "access restricted" from "page not found"
export const ALL_KNOWN_ROUTES: string[] = [
  ...new Set(Object.values(PERSONA_ALLOWED_ROUTES).flat()),
];

/**
 * Check if a path matches any known application route.
 * Returns true if the route belongs to any persona (even if current persona can't access it).
 * Returns false for completely unknown routes (404s).
 */
export function isKnownRoute(route: string): boolean {
  return ALL_KNOWN_ROUTES.some(
    known => route === known || route.startsWith(known + '/')
  );
}

/**
 * Returns routes that should be hidden/blocked based on disabled feature flags.
 */
function getFeatureFlagDisabledRoutes(): string[] {
  const { flags } = useFeatureFlagStore.getState();
  const disabled: string[] = [];
  if (!flags.data_library) disabled.push('/data-library');
  if (!flags.attendance_follow_ups) disabled.push('/absence-email-settings');
  return disabled;
}

interface PersonaState {
  activePersona: SwitchablePersona;
  isAdmin: boolean;
  isFullAdmin: boolean; // Only true for internal_admin, not internal_user (cohort manager)
}

interface PersonaActions {
  setActivePersona: (persona: SwitchablePersona) => void;
  resetToDefault: () => void;
  setIsAdmin: (isAdmin: boolean) => void;
  setIsFullAdmin: (isFullAdmin: boolean) => void;
  canAccessRoute: (route: string) => boolean;
  getNavItems: () => NavItem[];
  getPersonaConfig: () => PersonaConfig;
}

type PersonaStore = PersonaState & PersonaActions;

export const usePersonaStore = create<PersonaStore>()(
  persist(
    (set, get) => ({
      // State
      activePersona: 'admin',
      isAdmin: false,
      isFullAdmin: false,

      // Actions
      setActivePersona: (persona: SwitchablePersona) => {
        set({ activePersona: persona });
      },

      resetToDefault: () => {
        set({ activePersona: 'admin' });
      },

      setIsAdmin: (isAdmin: boolean) => {
        set({ isAdmin });
        // If not admin, reset to their actual persona
        if (!isAdmin) {
          set({ activePersona: 'admin' }); // Will be overridden by actual user persona
        }
      },

      setIsFullAdmin: (isFullAdmin: boolean) => {
        set({ isFullAdmin });
      },

      canAccessRoute: (route: string) => {
        const { activePersona } = get();
        const allowedRoutes = PERSONA_ALLOWED_ROUTES[activePersona];
        const disabledRoutes = getFeatureFlagDisabledRoutes();

        // Check if route starts with any allowed route AND is not disabled by feature flags
        return allowedRoutes.some(allowed =>
          route === allowed || route.startsWith(allowed + '/')
        ) && !disabledRoutes.some(disabled =>
          route === disabled || route.startsWith(disabled + '/')
        );
      },

      getNavItems: () => {
        const { activePersona } = get();
        const items = PERSONA_NAV_ITEMS[activePersona];
        const disabledRoutes = getFeatureFlagDisabledRoutes();

        // Filter out nav items whose routes are disabled by feature flags
        return items.filter(item => {
          if (item.path) {
            return !disabledRoutes.includes(item.path);
          }
          return true;
        });
      },

      getPersonaConfig: () => {
        const { activePersona } = get();
        return PERSONA_CONFIG[activePersona];
      },
    }),
    {
      name: 'persona-session',
      storage: createJSONStorage(() => sessionStorage), // Use sessionStorage for session-only persistence
      partialize: state => ({
        activePersona: state.activePersona,
        isAdmin: state.isAdmin,
        isFullAdmin: state.isFullAdmin,
      }),
    }
  )
);
